import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Smartphone, ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

function GoogleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29.1 5 24 5 12.9 5 4 13.9 4 25s8.9 20 20 20c11 0 19.5-8 19.5-20 0-1.3-.1-2.7-.4-4.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29.1 5 24 5 16.3 5 9.7 9.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 36 26.7 37 24 37c-5.3 0-9.7-3.6-11.3-8.4l-6.5 5C9.6 40.6 16.2 45 24 45z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41 36.9 44 31.4 44 25c0-1.3-.1-2.7-.4-4.5z" />
    </svg>
  );
}

export default function AuthDialog() {
  const { authOpen, closeAuth, login, setUser } = useAuth();
  const navigate = useNavigate();
  const [channel, setChannel] = useState("email"); // email | sms
  const [step, setStep] = useState("start"); // start | code
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const reset = () => {
    setStep("start"); setEmail(""); setPhone(""); setChallengeId(""); setCode("");
    setDevCode(""); setSmsConsent(false); setError(""); setInfo(""); setLoading(false);
  };
  const handleClose = () => { reset(); closeAuth(); };
  const switchChannel = (c) => { setChannel(c); setStep("start"); setError(""); setInfo(""); setCode(""); setDevCode(""); setSmsConsent(false); };

  const identValid = channel === "email" ? email.includes("@") : phone.replace(/\D/g, "").length >= 7;

  const requestCode = async (e) => {
    e?.preventDefault();
    setError(""); setInfo(""); setDevCode(""); setLoading(true);
    try {
      const payload = channel === "email" ? { channel, email } : { channel, phone, sms_consent: smsConsent };
      const { data } = await api.post("/auth/otp/request", payload);
      setChallengeId(data.challenge_id);
      setInfo(data.message);
      if (data.dev_code) setDevCode(data.dev_code);
      setStep("code");
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo enviar el código.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    try {
      const base = channel === "email" ? { channel, email } : { channel, phone };
      const { data } = await api.post("/auth/otp/verify", { ...base, challenge_id: challengeId, code });
      setUser(data);
      handleClose();
      navigate("/cuenta");
    } catch (err) {
      setError(err?.response?.data?.detail || "Código incorrecto.");
    } finally {
      setLoading(false);
    }
  };

  const Tab = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => switchChannel(id)}
      data-testid={`auth-tab-${id}`}
      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-[14px] border-2 border-[#12263A] font-bold text-[14px] transition-colors ${
        channel === id ? "bg-[#12263A] text-[#2E9EF0]" : "bg-white text-[#12263A] hover:bg-[#F3F2F2]"
      }`}
    >
      <Icon size={17} strokeWidth={2} /> {label}
    </button>
  );

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="auth-dialog"
        >
          <div className="absolute inset-0 bg-[#12263A]/70" onClick={handleClose} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white border-2 border-[#12263A] w-full max-w-md p-8 max-h-[92vh] overflow-y-auto"
            style={{ borderRadius: "28px" }}
          >
            <button
              onClick={handleClose} aria-label="Cerrar" data-testid="auth-close-btn"
              className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center border-2 border-[#12263A] rounded-[12px] text-[#12263A] hover:bg-[#F3F2F2] transition-colors"
            >
              <X size={18} strokeWidth={2.5} />
            </button>

            <Logo size={38} showText={false} />
            <h2 className="mt-5 text-[26px] font-black text-[#12263A] leading-tight" style={{ letterSpacing: "-0.02em" }}>
              Entra en Kiddio
            </h2>
            <p className="mt-1 text-[15px] text-[#2A3D52]">Crea tu cuenta o inicia sesión. Sin contraseñas.</p>

            <button
              onClick={() => login("/cuenta")} data-testid="auth-google-btn"
              className="mt-6 w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-[18px] bg-white border-2 border-[#12263A] font-bold text-[#12263A] hover:bg-[#F3F2F2] transition-colors"
            >
              <GoogleGlyph /> Continuar con Google
            </button>

            <div className="my-5 flex items-center gap-3">
              <span className="h-[2px] flex-1 bg-[#D6D9DC]" />
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6E7B8B]">o con un código</span>
              <span className="h-[2px] flex-1 bg-[#D6D9DC]" />
            </div>

            {/* Channel tabs */}
            <div className="flex gap-2" data-testid="auth-channel-tabs">
              <Tab id="email" icon={Mail} label="Correo" />
              <Tab id="sms" icon={Smartphone} label="SMS" />
            </div>

            <div className="mt-4">
              {step === "start" ? (
                <form onSubmit={requestCode} className="space-y-3">
                  {channel === "email" ? (
                    <>
                      <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6E7B8B]">Correo electrónico</label>
                      <div className="flex items-center gap-2 border-2 border-[#12263A] rounded-[16px] px-3">
                        <Mail size={18} strokeWidth={2} className="text-[#12263A]" />
                        <input
                          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                          placeholder="tu@correo.com" data-testid="auth-email-input"
                          className="flex-1 py-3 text-[15px] text-[#12263A] outline-none bg-transparent"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6E7B8B]">Número de teléfono</label>
                      <div className="flex items-center gap-2 border-2 border-[#12263A] rounded-[16px] px-3">
                        <Smartphone size={18} strokeWidth={2} className="text-[#12263A]" />
                        <input
                          type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)}
                          placeholder="+593 99 123 4567" data-testid="auth-phone-input"
                          className="flex-1 py-3 text-[15px] text-[#12263A] outline-none bg-transparent"
                        />
                      </div>
                      <p className="text-[12px] text-[#6E7B8B]">Incluye el código de país (por defecto +593).</p>
                      <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="auth-sms-consent-label">
                        <input
                          type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)}
                          data-testid="auth-sms-consent"
                          className="mt-0.5 w-5 h-5 shrink-0 accent-[#2E9EF0] border-2 border-[#12263A]"
                        />
                        <span className="text-[12px] leading-[1.5] text-[#2A3D52]">
                          Acepto recibir mensajes SMS de Kiddio en este número para códigos de verificación de un solo uso.
                          La frecuencia de mensajes puede variar. Pueden aplicarse tarifas de mensajes y datos. Responde
                          <strong className="text-[#12263A]"> STOP</strong> para dejar de recibir mensajes.
                        </span>
                      </label>
                    </>
                  )}
                  {error && <p className="text-[13px] font-bold text-[#FF6B57]" data-testid="auth-error">{error}</p>}
                  <button
                    type="submit" disabled={loading || !identValid || (channel === "sms" && !smsConsent)} data-testid="auth-send-code-btn"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-[18px] bg-[#12263A] text-white border-2 border-[#12263A] font-bold hover:bg-[#2E9EF0] hover:text-[#12263A] transition-colors disabled:opacity-60"
                  >
                    {loading ? "Enviando…" : channel === "sms" ? "Enviarme un SMS" : "Enviarme un código"} <ArrowRight size={18} strokeWidth={2.5} />
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyCode} className="space-y-3">
                  {info && <p className="text-[13px] text-[#3CB878] font-bold" data-testid="auth-info">{info}</p>}
                  {channel === "sms" && devCode && (
                    <div className="border-2 border-[#12263A] bg-[#FFC533] rounded-[14px] px-4 py-2.5" data-testid="auth-demo-code">
                      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#12263A]">Modo demo (Twilio pendiente)</span>
                      <p className="text-[15px] font-black text-[#12263A]">Tu código es: {devCode}</p>
                    </div>
                  )}
                  <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6E7B8B]">Código de 6 dígitos</label>
                  <input
                    inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••••" data-testid="auth-code-input"
                    className="w-full py-3 px-4 border-2 border-[#12263A] rounded-[16px] text-[24px] font-black tracking-[8px] text-center text-[#12263A] outline-none focus:border-[#2E9EF0]"
                  />
                  {error && <p className="text-[13px] font-bold text-[#FF6B57]" data-testid="auth-error">{error}</p>}
                  <button
                    type="submit" disabled={loading || code.length !== 6} data-testid="auth-verify-btn"
                    className="w-full px-5 py-3.5 rounded-[18px] bg-[#12263A] text-white border-2 border-[#12263A] font-bold hover:bg-[#2E9EF0] hover:text-[#12263A] transition-colors disabled:opacity-60"
                  >
                    {loading ? "Verificando…" : "Verificar y entrar"}
                  </button>
                  <button
                    type="button" onClick={() => { setStep("start"); setError(""); setCode(""); setDevCode(""); }}
                    data-testid="auth-back-btn"
                    className="w-full flex items-center justify-center gap-2 text-[14px] font-bold text-[#6E7B8B] hover:text-[#12263A] transition-colors"
                  >
                    <ArrowLeft size={16} strokeWidth={2.5} /> {channel === "sms" ? "Cambiar de número" : "Cambiar de correo"}
                  </button>
                </form>
              )}
            </div>

            <p className="mt-5 text-[12px] text-[#6E7B8B] leading-[1.5]">
              Al continuar aceptas nuestros{" "}
              <Link to="/terminos" onClick={handleClose} className="font-bold text-[#2E9EF0] underline">términos</Link>{" "}y la{" "}
              <Link to="/privacidad" onClick={handleClose} className="font-bold text-[#2E9EF0] underline">política de privacidad</Link>.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
